import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { VehicleConfirmationContext } from '../../../shared/contexts';
import { CRUMBS, EVENTS, TABS } from '../fitment-constants';

import { GET_VEHICLE_DETAILS } from '../../../shared/graphql/common-fitment-queries';

import { useFeatureFlags, useGraphQLClient } from '../../../shared/hooks/hooks';

import { FEATURE_FLAGS } from '../../../shared/constants/feature-flags';
import {
  ATV_VEHICLE_TYPE,
  RIDE_HEIGHT_TYPES,
  STOCKHEIGHT_DESCRIPTION,
  STOCKHEIGHT_ID
} from '../../../shared/constants/fitment';

export default function useVehicle({
  canToggleVehicle,
  getAssemblies,
  getMakes,
  getModels,
  getRideHeights,
  getTrims,
  getYears,
  isEditing,
  isFitmentConfirmed,
  isShowingRecentFitments,
  isTreadwellStart,
  isYearAfterModel,
  onEvent,
  pendingFitment,
  queryErrorEvent,
  requireFitmentConfirmation: requireFitmentConfirmationProp,
  selectedFitment,
  selectedTab,
  selectedVehicleCrumb,
  selectedVehicleDetails,
  setIsEditing,
  setIsShowingRecentFitments,
  setSelectedVehicleCrumb,
  shouldSkipSettingRideHeight
}) {
  const [assemblies, setAssemblies] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [selectedAssembly, setSelectedAssembly] = useState(null);
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedRideHeight, setSelectedRideHeight] = useState(null);
  const [selectedTrim, setSelectedTrim] = useState(null);
  const [selectedYear, setSelectedYear] = useState('');
  const [isPendingFitmentOptions, setIsPendingFitmentOptions] = useState(true);
  const [rideHeights, setRideHeights] = useState([]);
  const [trims, setTrims] = useState([]);
  const [years, setYears] = useState(
    [
      '2022',
      '2021',
      '2020',
      '2019',
      '2018',
      '2017',
      '2016',
      '2015',
      '2014',
      '2013',
      '2012',
      '2011',
      '2010',
      '2009'
    ].map(year => ({ description: year, id: year }))
  );
  const [isYearsFetched, setIsYearsFetched] = useState(false);
  const prevSelectedVehicleDetailsRef = useRef();
  const { featureFlags } = useFeatureFlags();
  const isTreadwellFitmentStepsReorder = Boolean(
    featureFlags?.[FEATURE_FLAGS.__FF_TREADWELL_FITMENT_STEPS_REORDER__]
  );

  const assembliesLength = assemblies?.length;
  const makesLength = makes?.length;
  const modelsLength = models?.length;
  const rideHeightsLength = rideHeights?.length;
  const trimsLength = trims?.length;
  const selectedFitmentUid = selectedFitment?.uid;
  const { updateConfirmationPath } = useContext(VehicleConfirmationContext);
  const requireFitmentConfirmation =
    typeof requireFitmentConfirmationProp === 'function'
      ? requireFitmentConfirmationProp(pendingFitment)
      : requireFitmentConfirmationProp;
  const client = useGraphQLClient();

  const resetAssemblies = useCallback(() => {
    setAssemblies([]);
    setSelectedAssembly(null);
  }, []);

  const resetMakes = useCallback(() => {
    setMakes([]);
    setSelectedMake('');
  }, []);

  const resetYears = useCallback(() => {
    setYears([]);
    setSelectedYear(null);
  }, []);

  const resetModels = useCallback(() => {
    setModels([]);
    setSelectedModel(null);
  }, []);

  const resetRideHeights = useCallback(() => {
    setRideHeights([]);
    setSelectedRideHeight(null);
  }, []);

  const resetTrims = useCallback(() => {
    setSelectedTrim(null);
    setTrims([]);
  }, []);

  useEffect(() => {
    const setVehicleDetails = async () => {
      const hasSelectedVehicleDetailsChanged =
        JSON.stringify(selectedVehicleDetails || {}) !==
        JSON.stringify(prevSelectedVehicleDetailsRef.current || {});

      if (hasSelectedVehicleDetailsChanged) {
        if (
          Object.keys(selectedVehicleDetails || {})?.some(
            key => selectedVehicleDetails[key] !== null
          )
        ) {
          prevSelectedVehicleDetailsRef.current = selectedVehicleDetails;

          let vehicleId = selectedVehicleDetails?.vehicleId;
          let trimId = selectedVehicleDetails?.trimId;
          let trimDesc = selectedVehicleDetails?.trimDesc;
          let make = selectedVehicleDetails?.make;
          let model = selectedVehicleDetails?.model;
          let assemblyId = selectedVehicleDetails?.assemblyId;
          let rideHeightId = selectedRideHeight?.id;
          let rideHeightDesc = selectedRideHeight?.description;
          let rideHeightType = selectedRideHeight?.type;

          if (!vehicleId || (selectedVehicleDetails?.trimDesc && !trimId)) {
            const details = await client
              .query({
                query: GET_VEHICLE_DETAILS,
                variables: {
                  forMake: selectedVehicleDetails.make,
                  forModel: selectedVehicleDetails.model,
                  forTrimDesc: selectedVehicleDetails.trimDesc,
                  forYear: selectedVehicleDetails.year
                }
              })
              .then(({ data }) => data?.vehicleSelection?.vehicleDetails);

            if (details) {
              vehicleId = details?.vehicleId;
              trimId = details?.trimId;
              trimDesc = details?.trimDesc;
              make = details?.make;
              model = details?.model;
            }
          }
          if (make) {
            setSelectedMake(make);
            setSelectedVehicleCrumb(
              process.env.__FF_FITMENT_VEHICLE_CRUMB__ === 'true'
                ? CRUMBS.VEHICLE
                : CRUMBS.MODEL
            );
          } else {
            resetMakes();
          }
          if (model) {
            setSelectedModel({
              description: model,
              id: isYearAfterModel ? model : vehicleId
            });

            if (isYearAfterModel) {
              process.env.__FF_FITMENT_VEHICLE_CRUMB__ !== 'true' &&
                setSelectedVehicleCrumb(CRUMBS.YEAR);
            } else {
              setSelectedVehicleCrumb(CRUMBS.TRIM);
            }
          } else {
            resetModels();
          }
          if (selectedVehicleDetails?.year) {
            setSelectedYear({
              description: selectedVehicleDetails?.year,
              id: isYearAfterModel ? vehicleId : selectedVehicleDetails?.year
            });
            const nextCrumb = () => {
              if (isYearAfterModel) {
                return CRUMBS.TRIM;
              }
              if (!selectedVehicleDetails?.make) {
                return process.env.__FF_FITMENT_VEHICLE_CRUMB__ === 'true'
                  ? CRUMBS.VEHICLE
                  : CRUMBS.MAKE;
              }
              if (!selectedVehicleDetails?.model) {
                return process.env.__FF_FITMENT_VEHICLE_CRUMB__ === 'true'
                  ? CRUMBS.VEHICLE
                  : CRUMBS.MODEL;
              }
              return CRUMBS.TRIM;
            };
            setSelectedVehicleCrumb(nextCrumb());
          } else {
            resetYears();
          }
          if (trimDesc) {
            setSelectedTrim({
              description: trimDesc,
              id: trimId
            });
            setSelectedVehicleCrumb(CRUMBS.TIRE_WHEEL_SIZE);
          } else {
            resetTrims();
            resetAssemblies();
          }
          if (rideHeightId) {
            setSelectedRideHeight({
              description: rideHeightDesc,
              id: rideHeightId,
              type: rideHeightType
            });
          } else {
            resetRideHeights();
          }

          if (assemblyId) {
            setSelectedAssembly({
              description: selectedVehicleDetails?.frontAssembly,
              id: selectedVehicleDetails?.assemblyId
            });
            if (!rideHeightId && !shouldSkipSettingRideHeight) {
              setSelectedRideHeight({
                description: STOCKHEIGHT_DESCRIPTION,
                id: STOCKHEIGHT_ID,
                type: RIDE_HEIGHT_TYPES.STOCKHEIGHT
              });
            }
          } else {
            resetAssemblies();
          }
        } else if (!isYearAfterModel) {
          setSelectedYear('');
          setSelectedVehicleCrumb(
            process.env.__FF_FITMENT_VEHICLE_CRUMB__ === 'true'
              ? CRUMBS.VEHICLE
              : CRUMBS.YEAR
          );
          resetMakes();
          resetModels();
          resetTrims();
        }
      }
    };
    setVehicleDetails();
  }, [
    resetAssemblies,
    resetMakes,
    resetModels,
    resetTrims,
    resetYears,
    selectedVehicleDetails,
    setSelectedVehicleCrumb,
    isYearAfterModel,
    client,
    resetRideHeights,
    shouldSkipSettingRideHeight,
    selectedRideHeight
  ]);

  useEffect(() => {
    async function updateFitment() {
      if (
        pendingFitment &&
        (!requireFitmentConfirmation ||
          (requireFitmentConfirmation && isFitmentConfirmed))
      ) {
        await onEvent({
          name: EVENTS.VEHICLE_FITMENT_UPDATED,
          payload: pendingFitment
        });
        if (isTreadwellStart) {
          if (pendingFitment?.type === ATV_VEHICLE_TYPE) {
            setSelectedVehicleCrumb(CRUMBS.TIRE_WHEEL_SIZE);
          } else {
            setSelectedVehicleCrumb(
              isTreadwellFitmentStepsReorder
                ? CRUMBS.DRIVING_TYPE
                : CRUMBS.DRIVING_LOCATION
            );
          }
        } else {
          setSelectedVehicleCrumb('');
        }
        setIsEditing(false);
        if (!canToggleVehicle) {
          setIsShowingRecentFitments(false);
        }
      }
    }

    updateFitment();
  }, [
    canToggleVehicle,
    isFitmentConfirmed,
    isTreadwellStart,
    onEvent,
    pendingFitment,
    requireFitmentConfirmation,
    setIsEditing,
    setSelectedVehicleCrumb,
    setIsShowingRecentFitments,
    selectedFitment,
    isTreadwellFitmentStepsReorder
  ]);

  const onFitment = useCallback(
    fitment => {
      onEvent({
        name: EVENTS.VEHICLE_FITMENT_UPDATE_PENDING,
        payload: fitment
      });
    },
    [onEvent]
  );

  const updateFitmentSelectionData = useCallback(
    fitment => {
      const { assemblyId, make, model, rideHeightId, trimId, vehicleId, year } =
        fitment || {};

      const fitmentPromise = Promise.all([
        getYears(make, model),
        getMakes(year),
        getModels(year, make),
        getTrims(vehicleId)
      ]).then(async ([years, makes, models, trims]) => {
        // The trimId on the assembly could be '1234', and we need this to match with the trim
        // with id '1234 - 2345'.  The dual trimIds are used by the backend to eliminate duplicate trims.
        const newSelectedTrim = Array.isArray(trims)
          ? trims.find(t => {
              if (typeof t?.id !== 'string') {
                return false;
              }

              const trimIds = t.id.split('-').map(t => t.trim());
              return trimIds.indexOf(trimId) > -1;
            })
          : null;

        const [rideHeights, assemblies] =
          (await Promise.all([
            getRideHeights(vehicleId, newSelectedTrim?.id),
            getAssemblies(newSelectedTrim?.id)
          ])) || [];

        setSelectedTrim(newSelectedTrim);

        return [years, makes, models, trims, rideHeights, assemblies];
      });

      return fitmentPromise.then(
        async ([years, makes, models, trims, rideHeights, assemblies]) => {
          if (isYearAfterModel) {
            setSelectedYear(
              Array.isArray(years) ? years.find(y => y?.id === vehicleId) : null
            );
          } else {
            setSelectedYear({ description: year, id: year });
          }
          setSelectedMake(make);
          const id = isYearAfterModel ? model : vehicleId;
          setSelectedModel(
            Array.isArray(models) ? models.find(m => m?.id === id) : null
          );
          setSelectedRideHeight(
            Array.isArray(rideHeights)
              ? rideHeights.find(r => r?.id === rideHeightId)
              : null
          );
          setSelectedAssembly(
            Array.isArray(assemblies)
              ? assemblies.find(a => a?.id === assemblyId)
              : null
          );
          setYears(years);
          setMakes(makes);
          setModels(models);
          setTrims(trims);
          setRideHeights(rideHeights);
          setAssemblies(assemblies);
          return fitment;
        }
      );
    },
    [
      getAssemblies,
      getMakes,
      getModels,
      getTrims,
      getRideHeights,
      getYears,
      isYearAfterModel,
      setYears
    ]
  );

  const onFitmentSelected = useCallback(
    fitment =>
      updateFitmentSelectionData(fitment).then(() => {
        if (!canToggleVehicle) {
          setIsShowingRecentFitments(false);
        }
        setSelectedVehicleCrumb('');
        updateConfirmationPath(location.pathname);
        return fitment;
      }),
    [
      updateFitmentSelectionData,
      canToggleVehicle,
      setSelectedVehicleCrumb,
      updateConfirmationPath,
      setIsShowingRecentFitments
    ]
  );

  useEffect(() => {
    const shouldFetchYears =
      (!isYearsFetched && !isYearAfterModel) ||
      (isYearAfterModel && selectedMake && selectedModel);

    if (selectedTab !== TABS.VEHICLE) return;
    if (
      (selectedVehicleCrumb === CRUMBS.YEAR ||
        selectedVehicleCrumb === CRUMBS.VEHICLE) &&
      shouldFetchYears
    ) {
      getYears(selectedMake, selectedModel?.description)
        .then(setYears)
        .catch(queryErrorEvent(EVENTS.ERROR_QUERY_GET_YEARS));
      setIsYearsFetched(true);
    }
  }, [
    getYears,
    isYearsFetched,
    queryErrorEvent,
    selectedTab,
    selectedVehicleCrumb,
    isYearAfterModel,
    selectedMake,
    selectedModel
  ]);

  useEffect(() => {
    if (selectedFitment) {
      setIsPendingFitmentOptions(true);
    }
  }, [selectedFitment]);

  useEffect(() => {
    if (
      isPendingFitmentOptions &&
      selectedFitment &&
      selectedVehicleCrumb &&
      isEditing
    ) {
      updateFitmentSelectionData(selectedFitment);
      setIsPendingFitmentOptions(false);
    }
  }, [
    isPendingFitmentOptions,
    selectedFitment,
    selectedVehicleCrumb,
    updateFitmentSelectionData,
    isEditing
  ]);

  useEffect(() => {
    if (selectedTab !== TABS.VEHICLE) return;
    if (
      !isShowingRecentFitments &&
      !selectedVehicleCrumb &&
      selectedFitment &&
      !assembliesLength
    ) {
      onFitmentSelected(selectedFitment);
    }
  }, [
    isShowingRecentFitments,
    selectedVehicleCrumb,
    selectedFitment,
    assembliesLength,
    selectedTab,
    onFitmentSelected
  ]);

  useEffect(() => {
    if (selectedTab !== TABS.VEHICLE) return;
    if (
      (selectedVehicleCrumb === CRUMBS.MAKE ||
        selectedVehicleCrumb === CRUMBS.VEHICLE) &&
      (isYearAfterModel || selectedYear) &&
      !makesLength
    ) {
      getMakes(selectedYear?.description)
        .then(setMakes)
        .catch(queryErrorEvent(EVENTS.ERROR_QUERY_GET_MAKES));
    }
  }, [
    getMakes,
    makesLength,
    queryErrorEvent,
    selectedTab,
    selectedVehicleCrumb,
    selectedYear,
    isYearAfterModel
  ]);

  useEffect(() => {
    if (selectedTab !== TABS.VEHICLE) return;
    if (
      (selectedVehicleCrumb === CRUMBS.MODEL ||
        selectedVehicleCrumb === CRUMBS.VEHICLE) &&
      (isYearAfterModel || selectedYear) &&
      selectedMake &&
      !modelsLength
    ) {
      getModels(selectedYear?.description, selectedMake)
        .then(setModels)
        .catch(queryErrorEvent(EVENTS.ERROR_QUERY_GET_MODELS));
    }
  }, [
    selectedTab,
    selectedVehicleCrumb,
    selectedYear,
    selectedMake,
    modelsLength,
    getModels,
    queryErrorEvent,
    isYearAfterModel
  ]);

  useEffect(() => {
    if (selectedTab !== TABS.VEHICLE) return;
    if (selectedVehicleCrumb === CRUMBS.TRIM && selectedModel && !trimsLength) {
      getTrims(isYearAfterModel ? selectedYear?.id : selectedModel.id)
        .then(setTrims)
        .catch(queryErrorEvent(EVENTS.ERROR_QUERY_GET_TRIMS));
    }
  }, [
    selectedTab,
    selectedVehicleCrumb,
    selectedYear,
    selectedMake,
    selectedModel,
    trimsLength,
    getTrims,
    queryErrorEvent,
    isYearAfterModel
  ]);

  useEffect(() => {
    if (selectedTab !== TABS.VEHICLE) return;
    if (
      (selectedVehicleCrumb === CRUMBS.TRIM ||
        selectedVehicleCrumb === CRUMBS.TIRE_WHEEL_SIZE) &&
      selectedModel &&
      selectedTrim &&
      !rideHeightsLength
    ) {
      getRideHeights(
        selectedVehicleDetails?.vehicleId || selectedModel.id,
        selectedTrim.id
      )
        .then(setRideHeights)
        .catch(queryErrorEvent(EVENTS.ERROR_QUERY_GET_RIDE_HEIGHTS));
    }
  }, [
    selectedTab,
    selectedVehicleCrumb,
    selectedModel,
    selectedTrim,
    rideHeightsLength,
    getRideHeights,
    queryErrorEvent,
    resetRideHeights,
    rideHeights,
    selectedVehicleDetails,
    selectedFitment
  ]);

  useEffect(() => {
    if (selectedTab !== TABS.VEHICLE) return;
    onEvent({
      name: EVENTS.VEHICLE_FITMENT_ACTIVE_CRUMB,
      payload: selectedVehicleCrumb
    });
    if (
      selectedVehicleCrumb === CRUMBS.TIRE_WHEEL_SIZE &&
      selectedTrim &&
      selectedRideHeight &&
      !pendingFitment &&
      !assembliesLength
    ) {
      getAssemblies(selectedTrim.id)
        .then(assemblies => {
          setAssemblies(assemblies);
          if (Array.isArray(assemblies) && assemblies.length === 1) {
            setSelectedAssembly(assemblies[0]);
          }
        })
        .catch(queryErrorEvent(EVENTS.ERROR_QUERY_GET_ASSEMBLIES));
    }
  }, [
    pendingFitment,
    selectedTab,
    selectedVehicleCrumb,
    selectedYear,
    selectedMake,
    selectedModel,
    selectedTrim,
    selectedRideHeight,
    assembliesLength,
    getAssemblies,
    queryErrorEvent,
    isEditing,
    selectedFitmentUid,
    onFitment,
    onEvent
  ]);

  return {
    assemblies,
    makes,
    models,
    onFitment,
    onFitmentSelected,
    pendingFitment,
    resetAssemblies,
    resetMakes,
    resetModels,
    resetRideHeights,
    resetTrims,
    resetYears,
    rideHeights,
    selectedAssembly,
    selectedMake,
    selectedModel,
    selectedRideHeight,
    selectedTrim,
    selectedYear,
    setSelectedAssembly,
    setSelectedMake,
    setSelectedModel,
    setSelectedRideHeight,
    setSelectedTrim,
    setSelectedYear,
    trims,
    updateFitmentSelectionData,
    years
  };
}
